import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const ShippingPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      <header className="bg-white shadow-sm border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition">
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Application Processing Policy</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This is an educational institution admission portal. This policy describes our application processing and communication delivery timelines, not physical shipping of goods.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>1. Application Processing Timeline</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Immediate Processing:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Payment confirmation: Instant (within 1-2 minutes)</li>
              <li>Login credentials delivery: Immediate via email and SMS</li>
              <li>Application form access: Instant after payment confirmation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Application Review:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Initial review: Within 2-3 business days of submission</li>
              <li>Document verification: 3-5 business days</li>
              <li>Interview scheduling: 5-7 business days after submission</li>
              <li>Final decision: Within 2 weeks of interview completion</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>2. Credential Delivery</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Digital Delivery Methods:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Email:</strong> Login credentials sent to registered email address within 5 minutes of payment</li>
              <li><strong>SMS:</strong> Backup credentials sent via SMS to registered mobile number</li>
              <li><strong>Portal Access:</strong> Immediate access to application form after successful login</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Delivery Issues:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>If you don't receive credentials within 10 minutes, check spam/junk folder</li>
              <li>Ensure correct email and phone number were provided during payment</li>
              <li>Contact support through our Contact Us page for assistance</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>3. Application Status Updates</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Notification Timelines:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Application Received:</strong> Instant confirmation upon submission</li>
              <li><strong>Under Review:</strong> Updated within 2-3 business days</li>
              <li><strong>Interview Scheduled:</strong> Notified 24-48 hours before interview date</li>
              <li><strong>Final Decision:</strong> Communicated within 24 hours of decision</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              All status updates are sent via:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Email to registered address</li>
              <li>SMS to registered mobile number</li>
              <li>Real-time update in parent dashboard</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>4. Interview Notification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Scheduling Process:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Interview slots available: 10:00 AM to 2:00 PM (15-minute intervals)</li>
              <li>Notification sent: Minimum 48 hours before scheduled time</li>
              <li>Confirmation required: Please respond to interview invitation</li>
              <li>Rescheduling: Contact school administration at least 24 hours in advance</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>5. Document Submission</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Digital Submission:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Upload documents directly through the portal</li>
              <li>Supported formats: PDF, JPG, PNG (max 5MB per file)</li>
              <li>Instant upload confirmation</li>
              <li>Documents accessible to school administration immediately</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Physical Verification:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Original documents must be presented during interview</li>
              <li>Photocopies may be collected for school records</li>
              <li>Documents will be verified on the spot</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>6. Communication Channels</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Primary Channels:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Email:</strong> Official communications sent to registered email</li>
              <li><strong>SMS:</strong> Critical updates and reminders via SMS</li>
              <li><strong>Portal Dashboard:</strong> Real-time status visible in parent dashboard</li>
              <li><strong>Phone:</strong> School office hours for urgent queries</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>7. Processing Delays</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Possible Delays Due To:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Incomplete application forms</li>
              <li>Missing or unclear documents</li>
              <li>High volume of applications during peak admission season</li>
              <li>School holidays or weekends (processing resumes next business day)</li>
              <li>Technical issues (rare, resolved within 24 hours)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              In case of delays, parents will be notified via email with expected resolution time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>8. Business Days</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Business days are Monday to Friday, excluding:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>National holidays</li>
              <li>School holidays</li>
              <li>Weekends (Saturday and Sunday)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Applications submitted on non-business days will be processed on the next business day.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>9. Support and Assistance</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For any questions or concerns regarding application processing:
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li>Visit our Contact Us page</li>
              <li>Email: Available on Contact Us page</li>
              <li>Phone: Available during school office hours</li>
              <li>Response time: Within 24 hours on business days</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>10. Admission Outcome</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Final Communication:</strong>
            </p>
            <ul className="list-disc ml-6 text-gray-700 mb-4">
              <li><strong>Acceptance:</strong> Admission letter sent via email within 24 hours of decision</li>
              <li><strong>Waitlist:</strong> Status updated in portal, email notification sent</li>
              <li><strong>Rejection:</strong> Polite notification sent via email</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              All decisions are final and communicated in writing through the registered email address.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>11. Policy Updates</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This policy may be updated to reflect changes in our processes. Users will be notified of significant changes via email. The "Last Updated" date indicates the most recent revision.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;